import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGetDashboard, apiTodayFollowups } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

/* ─── Tiny bar chart (pure CSS) ─────────────────────────────────────────── */
function BarChart({ data }) {
    if (!data || !data.length) return <div className="empty-state"><p>No data</p></div>;
    const max = Math.max(...data.map(d => d.count), 1);
    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '160px', padding: '0 4px' }}>
            {data.map((d, i) => {
                const pct = (d.count / max) * 100;
                const label = new Date(d.date).toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' });
                return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', height: '100%', justifyContent: 'flex-end' }}>
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 600 }}>{d.count}</span>
                        <div
                            title={`${label}: ${d.count} leads`}
                            style={{
                                width: '100%', borderRadius: '4px 4px 0 0',
                                height: `${Math.max(pct, 4)}%`,
                                background: 'linear-gradient(180deg, var(--accent-light), var(--accent))',
                                transition: 'height 0.5s ease',
                                cursor: 'default',
                            }}
                        />
                        <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: '100%', textAlign: 'center' }}>
                            {new Date(d.date).toLocaleDateString('en', { weekday: 'short' })}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

/* ─── Donut chart (SVG) ──────────────────────────────────────────────────── */
const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6', '#06b6d4'];
function DonutChart({ data, label }) {
    if (!data || !data.length) return <div className="empty-state"><p>No data</p></div>;
    const total = data.reduce((s, d) => s + d.count, 0) || 1;
    const r = 60, cx = 80, cy = 80, stroke = 22;
    const circ = 2 * Math.PI * r;
    let offset = 0;
    const segments = data.map((d, i) => {
        const pct = d.count / total;
        const dash = pct * circ;
        const seg = { pct, dash, offset, color: COLORS[i % COLORS.length], label: d.source || d.status, count: d.count };
        offset += dash;
        return seg;
    });

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
            <svg width="160" height="160" style={{ flexShrink: 0 }}>
                <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--bg-elevated)" strokeWidth={stroke} />
                {segments.map((seg, i) => (
                    <circle key={i} cx={cx} cy={cy} r={r} fill="none"
                        stroke={seg.color} strokeWidth={stroke}
                        strokeDasharray={`${seg.dash} ${circ - seg.dash}`}
                        strokeDashoffset={-seg.offset + circ / 4}
                        style={{ transition: 'stroke-dasharray 0.5s' }}
                    />
                ))}
                <text x={cx} y={cy - 6} textAnchor="middle" fill="var(--text-primary)" fontSize="20" fontWeight="700">{total}</text>
                <text x={cx} y={cy + 14} textAnchor="middle" fill="var(--text-muted)" fontSize="10">{label}</text>
            </svg>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {segments.map((seg, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.78rem' }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: seg.color, flexShrink: 0 }} />
                        <span style={{ color: 'var(--text-secondary)', flex: 1 }}>{seg.label}</span>
                        <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{seg.count}</span>
                        <span style={{ color: 'var(--text-muted)' }}>({(seg.pct * 100).toFixed(0)}%)</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function DashboardPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [todayFu, setTodayFu] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [dash, fu] = await Promise.all([apiGetDashboard(), apiTodayFollowups()]);
            setData(dash.data);
            setTodayFu(fu.data?.data || []);
        } catch (e) {
            toast.error('Failed to load dashboard');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="loading-center" style={{ minHeight: '80vh' }}><div className="spinner" /><span>Loading dashboard…</span></div>;

    const stats = data?.stats || {};
    const charts = data?.charts || {};

    const STAT_CARDS = [
        { label: 'Total Leads', value: stats.total_leads ?? 0, icon: '👥', color: 'blue' },
        { label: 'New Today', value: stats.new_today ?? 0, icon: '⚡', color: 'purple' },
        { label: 'Converted', value: stats.converted ?? 0, icon: '✅', color: 'green' },
        { label: 'Follow-ups Today', value: stats.followups_today ?? 0, icon: '📅', color: 'orange' },
        { label: 'In Follow-up', value: stats.in_followup ?? 0, icon: '🔄', color: 'cyan' },
        { label: 'Lost / Not Interested', value: stats.lost ?? 0, icon: '❌', color: 'red' },
    ];

    return (
        <div className="page">
            {/* Header */}
            <div className="page-header">
                <div>
                    <h1 className="page-title">Dashboard</h1>
                    <p className="page-subtitle">Welcome back, {user?.name}! Here's what's happening today.</p>
                </div>
                <button id="dash-add-lead" className="btn btn-primary" onClick={() => navigate('/leads/new')}>
                    ➕ Add Lead
                </button>
            </div>

            {/* Today's Follow-up banner */}
            {todayFu.length > 0 && (
                <div
                    className="notification-bar"
                    id="today-followup-banner"
                    onClick={() => navigate('/followups')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && navigate('/followups')}
                >
                    <span>📅 You have <strong>{todayFu.length}</strong> pending follow-up{todayFu.length !== 1 ? 's' : ''} today — click to view</span>
                    <span>→</span>
                </div>
            )}

            {/* Stats */}
            <div className="stats-grid">
                {STAT_CARDS.map(c => (
                    <div key={c.label} className={`stat-card stat-card--${c.color}`} id={`stat-${c.label.replace(/\s+/g, '-').toLowerCase()}`}>
                        <div className="stat-icon">{c.icon}</div>
                        <div>
                            <span className="stat-value">{c.value}</span>
                            <span className="stat-label">{c.label}</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Charts */}
            <div className="dashboard-grid">
                <div className="card">
                    <div className="card-header">
                        <span className="card-title">📈 Leads — Last 7 Days</span>
                    </div>
                    <div className="card-body">
                        <BarChart data={charts.leadsPerDay} />
                    </div>
                </div>

                <div className="card">
                    <div className="card-header">
                        <span className="card-title">📊 Lead Status Distribution</span>
                    </div>
                    <div className="card-body">
                        <DonutChart data={charts.statusData} label="Leads" />
                    </div>
                </div>

                <div className="card">
                    <div className="card-header">
                        <span className="card-title">🌐 Lead Sources</span>
                    </div>
                    <div className="card-body">
                        <DonutChart data={charts.sourceData} label="Sources" />
                    </div>
                </div>

                <div className="card">
                    <div className="card-header">
                        <span className="card-title">📅 Today's Follow-ups</span>
                        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/followups')}>View All</button>
                    </div>
                    {todayFu.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-state-icon">🎉</div>
                            <p>No follow-ups due today!</p>
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Lead</th>
                                        <th>Phone</th>
                                        <th>Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {todayFu.slice(0, 5).map(f => (
                                        <tr key={f.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/leads/${f.lead_id}`)}>
                                            <td><strong>{f.lead_name || '—'}</strong></td>
                                            <td>{f.lead_phone || '—'}</td>
                                            <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.notes || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
